import React, { useEffect, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import makeStyles from "@mui/styles/makeStyles";
import { Alert, Card, CardContent, CardHeader, Grid, Typography } from "@mui/material";

/** Redux */
import { useSelector } from "react-redux";
import { getLayout, getPageLength } from "../../../_appStore/DB_Settings_Table";
import { getLimits, getPrediction } from "../../../_appStore/DB_Common";

/** Helpers */
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/** UI */
const ListJT = dynamic(import("./ListComponents/ListJT"));
const ListSC = dynamic(import("./ListComponents/ListSC"));
const ListWSK = dynamic(import("./ListComponents/ListWSK"));
const TableActionsMenu = dynamic(() => import("./Actions/TableActionsMenu"));
const TableSettings = dynamic(() => import("./Setting/_TableSettings"));
const Legend = dynamic(() => import("./Legend/Legend"));
const MuiTableIndex = dynamic(() => import("./MuiTable/MuiTable_index"));

const useStyles = makeStyles((theme) => ({
    boxedWrapper: {
        ...theme.shape.boxedWrapper,
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2, 1),
        },
    },
    Card: {
        "&:focus-visible": {
            ...theme.outliness,
        },
    },
    srOnly: {
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        border: 0,
    },
    CardHeaderRoot: {
        paddingTop: theme.spacing(0),
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 0),
        },
    },
    CardHeaderAction: {
        position: "relative",
    },
    CardHeaderTitle: {},
    CardHeaderSubheader: {
        color: "#F00",
        fontSize: "1rem",
        textAlign: "center",
    },
    CardContentRoot: {
        // maxHeight: 444,
        overflow: "auto",
        overflowX: "hidden",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2, 0),
        },
    },
    tabelTitle: {
        fontSize: "1.1111111rem",
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        textAlign: "center",
        "&:focus-visible": {
            ...theme.outliness,
        },
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.88888889rem",
            textAlign: "left",
        },
    },
}));

const DBTable = (props) => {
    const { loadingDefaults, isToBeApplied, onApply, recordsCount, onPredictionData } = props;
    const classes = useStyles();
    const { t } = useTranslation();

    /** States */
    const [ABORT, setABORT] = useState(false);
    const [HEAD_DATA, setHEAD_DATA] = useState(null);
    const [LEGEND, setLEGEND] = useState(null);
    const [RECORDS_COUNT, setRECORDS_COUNT] = useState(0);
    const [REQUEST, setREQUEST] = useState(null);
    const [ZOOM, setZOOM] = useState(false);

    /** Redux selectors */
    const layout = useSelector(getLayout);
    const limits = useSelector(getLimits);
    const pageLength = useSelector(getPageLength);
    const prediction = useSelector(getPrediction);

    /** Effects */
    useEffect(() => {
        setRECORDS_COUNT(prediction?.table);
    }, [prediction]);

    /** UI */
    const ListComponent = (props) => {
        const { type, ...rest } = props;
        if (!type) return null;
        switch (type.toUpperCase()) {
            case "JT":
                return <ListJT {...rest} />;
            case "SC":
                return <ListSC {...rest} />;
            case "ZM":
                return <ListWSK {...rest} />;
            default:
                break;
        }
    };

    /** Handlers */
    const handleOnGetHeadData = ({ headData }) => setHEAD_DATA(headData);

    const handleOnGetLegendData = ({ legend }) => setLEGEND(legend);

    const handleOnZoom = ({ zoom }) => setZOOM(zoom);

    const handleGetRequest = ({ request }) => setREQUEST(request);

    const handleOnAbortApi = (aborted) => {
        setABORT(aborted);
    };

    return (
        <Card className={clsx(classes.Card, classes.boxedWrapper)} id={"db-table-card"} tabIndex={-1}>
            {/* <pre>{JSON.stringify({ ABORT })}</pre> */}
            {/* <h2 className={classes.srOnly}>{t("Tablica{{dot}}", { dot: "." })}</h2> */}

            {!!ABORT && (
                <Alert severity={"warning"} sx={{ "&, & *": { color: "#663D00", backgroundColor: "#FFF5E6"}," & .MuiAlert-message":{ fontSize:'1rem'}  }}>
                    {t(
                        `Pobieranie danych zatrzymane{{dot}} Wybierz nową zmienną lub zaznacz elementy w polach filtrów lub wyboru zaawansowanego{{dot}}`,
                        { dot: "." }
                    )}
                </Alert>
            )}

            {!ABORT && (
                <CardHeader
                    action={
                        <div role={"menu"}>
                            <TableSettings
                                headData={HEAD_DATA}
                                isToBeApplied={isToBeApplied}
                                onPredictionData={(data) => onPredictionData(data)}
                                type={"options"}
                            />
                            <TableSettings
                                headData={HEAD_DATA}
                                isToBeApplied={isToBeApplied}
                                onPredictionData={(data) => onPredictionData(data)}
                                type={"sort"}
                            />
                            <TableActionsMenu onZoom={(zoom) => handleOnZoom({ zoom })} request={REQUEST} />
                        </div>
                    }
                    subheader={
                        <>
                            {layout.list.length ? (
                                <ListComponent type={layout.list[0]} isToBeApplied={isToBeApplied} />
                            ) : (
                                ""
                            )}
                        </>
                    }
                    title={
                        <>
                            <Typography
                                className={classes.tabelTitle}
                                component={"h2"}
                                id={"db-table-title"}
                                tabIndex={-1}
                            >
                                {t("Tablica wygenerowana na podstawie ustawień użytkownika z Dziedzinowych Baz Wiedzy")}
                            </Typography>

                            {!!RECORDS_COUNT?._accepted ? (
                                ""
                            ) : RECORDS_COUNT?._errMessage ? (
                                <Alert
                                    severity="error"
                                    // variant="outlined"
                                    sx={{ alignItems: RECORDS_COUNT?._notes ? "flex-start" : "center", mb: 3 }}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: RECORDS_COUNT?._errMessage,
                                        }}
                                    />
                                    {!!RECORDS_COUNT?._notes && (
                                        <span
                                            style={{
                                                display: "block",
                                                width: "100%",
                                                textAlign: "left",
                                                marginTop: 16,
                                                borderTop: "1px solid #CCC",
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: RECORDS_COUNT?._notes,
                                            }}
                                        />
                                    )}
                                </Alert>
                            ) : null}
                        </>
                    }
                    classes={{
                        action: classes.CardHeaderAction,
                        title: classes.CardHeaderTitle,
                        subheader: classes.CardHeaderSubheader,
                        root: classes.CardHeaderRoot,
                    }}
                    // className={classes.CardHeaderRoot}
                />
            )}

            <CardContent
                classes={{ root: classes.CardContentRoot }}
                style={{ padding: !!ABORT ? 0 : undefined, height: !!ABORT ? 0 : undefined }}
            >
                <Grid
                    container
                    spacing={0}
                    sx={
                        {
                            /*overflowX: "hidden"*/
                        }
                    }
                >
                    <Grid item xs={12}>
                        <MuiTableIndex
                            loadingDefaults={loadingDefaults}
                            onGetHeadData={(headData) => handleOnGetHeadData({ headData })}
                            onGetLegendData={(legend) => handleOnGetLegendData({ legend })}
                            onZoom={(zoom) => handleOnZoom({ zoom })}
                            pageLength={pageLength}
                            zoom={ZOOM}
                            onGetRequest={(request) => handleGetRequest({ request })}
                            onApply={onApply}
                            onAbortAPI={handleOnAbortApi}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        {!!LEGEND && !ABORT && <Legend legend={LEGEND} />}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default DBTable;
